interface QuillResizeImageOptions {
    locale?: {
        floatLeft?: string;
        floatRight?: string;
        center?: string;
        restore?: string;
        altTip?: string;
        inputTip?: string;
    };
    onChange?: (element: HTMLElement) => void;
}

interface ResizePosition {
    left: number;
    top: number;
    width: number;
    height: number;
}

function styleInject(css: string): void {
    if (!css) return;
    if (typeof window === 'undefined') return;
    
    const style = document.createElement('style');
    style.setAttribute('media', 'screen');
    style.innerHTML = css;
    document.head.appendChild(style);
}

styleInject(`
#editor-resizer {
  position: absolute;
  border: 1px dashed #fff;
  background-color: rgba(0, 0, 0, 0.5);
}
#editor-resizer .handler {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 10px;
  height: 10px;
  border: 1px solid #333;
  background-color: rgba(255, 255, 255, 0.8);
  cursor: nwse-resize;
  user-select: none;
}
#editor-resizer .toolbar {
  position: absolute;
  top: -5em;
  left: 50%;
  padding: 0.5em;
  border: 1px solid #fff;
  border-radius: 3px;
  background-color: #fff;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
  transform: translateX(-50%);
  width: 20em;
}
#editor-resizer .toolbar .group {
  display: flex;
  border: 1px solid #aaa;
  border-radius: 6px;
  white-space: nowrap;
  text-align: center;
  line-height: 2;
  color: rgba(0, 0, 0, 0.65);
}
#editor-resizer .toolbar .group:not(:first-child) {
  margin-top: 0.5em;
}
#editor-resizer .toolbar .group .btn {
  flex: 1 0 0;
  text-align: center;
  width: 25%;
  padding: 0 0.5rem;
  display: inline-block;
  vertical-align: top;
  user-select: none;
  color: inherit;
}
#editor-resizer .toolbar .group .btn:not(:last-child) {
  border-right: 1px solid #bbb;
}
#editor-resizer .toolbar .group .btn:not(.btn-group):active {
  background-color: rgba(0, 0, 0, 0.1);
}
#editor-resizer .toolbar .group .input-wrapper {
  width: 25%;
  border: 1px solid #eee;
  position: relative;
  border-right: 1px solid #bbb;
  min-width: 4em;
}
#editor-resizer .toolbar .group .input-wrapper::after {
  content: " ";
  position: absolute;
  height: 1px;
  background-color: #333;
  left: 0.5em;
  right: 1em;
  bottom: 0.2em;
}
#editor-resizer .toolbar .group .input-wrapper input {
  color: inherit;
  text-align: center;
  width: 100%;
  border: none;
  outline: none;
  padding: 0 0.5em;
  padding-right: 1.5em;
}
#editor-resizer .toolbar .group .input-wrapper input:focus ~ .tooltip {
  display: block;
}
#editor-resizer .toolbar .group .input-wrapper .suffix {
  position: absolute;
  right: 0.5em;
}
#editor-resizer .toolbar .group .input-wrapper .tooltip {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  font-size: small;
  background-color: #fff;
  box-shadow: 0 0 3px #a7a7a7;
  padding: 0 0.6em;
  border-radius: 5px;
  zoom: 0.85;
}
#editor-resizer .toolbar .group .alt-input {
  width: 100%;
  border: 1px solid #eee;
  position: relative;
  min-width: 8em;
}
#editor-resizer .toolbar .group .alt-input::after {
  display: none;
}
#editor-resizer .toolbar .group .alt-input input {
  color: inherit;
  text-align: left;
  width: 100%;
  border: none;
  outline: none;
  padding: 0 0.5em;
  padding-right: 0.5em;
}
`);

class I18n {
    private config: any;

    constructor(config?: any) {
        this.config = { ...defaultLocale, ...config };
    }

    findLabel(key: string): string | null {
        if (this.config) {
            return this.config[key] || null;
        }
        return null;
    }
}

const defaultLocale = {
    floatLeft: "left",
    floatRight: "right", 
    center: "center",
    restore: "restore",
    altTip: "Press and hold alt to lock ratio!",
    inputTip: "Press enter key to apply change!",
};

function format(str: string, ...values: string[]): string {
    return str.replace(/\{(\d+)\}/g, (match, index) => {
        const idx = parseInt(index);
        if (values.length > idx) {
            return values[idx];
        }
        return "";
    });
}

const template = `
<div class="handler" title="{0}"></div>
<div class="toolbar">
  <div class="group">
    <a class="btn" data-type="width" data-styles="width:100%">100%</a>
    <a class="btn" data-type="width" data-styles="width:50%">50%</a>
    <span class="input-wrapper"><input data-type="width" maxlength="3" /><span class="suffix">%</span><span class="tooltip">{5}</span></span>
    <a class="btn" data-type="width" data-styles="width:auto">{4}</a>
  </div>
  <div class="group">
    <a class="btn" data-type="align" data-styles="float:left">{1}</a>
    <a class="btn" data-type="align" data-styles="display:block;margin:auto;">{2}</a>
    <a class="btn" data-type="align" data-styles="float:right;">{3}</a>
    <a class="btn" data-type="align" data-styles="">{4}</a>
  </div>
  <div class="group">
    <span class="input-wrapper alt-input"><input data-type="alt" placeholder="Alt text" maxlength="200" /><span class="tooltip">Enter alt text for accessibility</span></span>
  </div>
</div>
`;

class ResizePlugin {
    public resizer: HTMLElement | null = null;
    private startResizePosition: ResizePosition | null = null;
    private i18n: I18n;
    private options?: QuillResizeImageOptions;
    private resizeTarget: HTMLElement;
    private editor: HTMLElement;
    private container: HTMLElement;

    constructor(resizeTarget: HTMLElement, container: HTMLElement, editor: HTMLElement, options?: QuillResizeImageOptions) {
        this.i18n = new I18n(options?.locale || defaultLocale);
        this.options = options;
        this.resizeTarget = resizeTarget;
        
        if (!(resizeTarget as any).originSize) {
            (resizeTarget as any).originSize = {
                width: resizeTarget.clientWidth,
                height: resizeTarget.clientHeight,
            };
        }
        
        this.editor = editor;
        this.container = container;
        this.initResizer();
        this.positionResizerToTarget(resizeTarget);
        
        this.resizing = this.resizing.bind(this);
        this.endResize = this.endResize.bind(this);
        this.startResize = this.startResize.bind(this);
        this.toolbarClick = this.toolbarClick.bind(this);
        this.toolbarInputChange = this.toolbarInputChange.bind(this);
        this.toolbarAltChange = this.toolbarAltChange.bind(this);
        this.onScroll = this.onScroll.bind(this);
        
        this.bindEvents();
    }

    private initResizer(): void {
        let resizer = this.container.querySelector("#editor-resizer") as HTMLElement;
        if (!resizer) {
            resizer = document.createElement("div");
            resizer.setAttribute("id", "editor-resizer");
            resizer.innerHTML = format(
                template, 
                this.i18n.findLabel("altTip") || "", 
                this.i18n.findLabel("floatLeft") || "", 
                this.i18n.findLabel("center") || "", 
                this.i18n.findLabel("floatRight") || "", 
                this.i18n.findLabel("restore") || "", 
                this.i18n.findLabel("inputTip") || ""
            );
            this.container.appendChild(resizer);
        }
        this.resizer = resizer;
        
        const altInput = this.resizer.querySelector('[data-type="alt"]') as HTMLInputElement;
        if (altInput && this.resizeTarget) {
            altInput.value = this.resizeTarget.getAttribute("alt") || "";
        }
    }

    private positionResizerToTarget(el: HTMLElement): void {
        if (this.resizer !== null) {
            this.resizer.style.setProperty("left", el.offsetLeft + "px");
            this.resizer.style.setProperty("top", (el.offsetTop - this.editor.scrollTop) + "px");
            this.resizer.style.setProperty("width", el.clientWidth + "px");
            this.resizer.style.setProperty("height", el.clientHeight + "px");
        }
    }

    private bindEvents(): void {
        if (this.resizer !== null) {
            this.resizer.addEventListener("mousedown", this.startResize);
            this.resizer.addEventListener("click", this.toolbarClick);
            this.resizer.addEventListener("change", this.toolbarInputChange);
            this.resizer.addEventListener("input", this.toolbarAltChange);
        }
        window.addEventListener("mouseup", this.endResize);
        window.addEventListener("mousemove", this.resizing);
        this.editor.addEventListener('scroll', this.onScroll);
    }

    private onScroll(): void {
        this.positionResizerToTarget(this.resizeTarget);
    }

    private _setStylesForToolbar(type: string, styles: string): void {
        const storeKey = "_styles_" + type;
        const style = this.resizeTarget.style;
        const originStyles = (this.resizeTarget as any)[storeKey] || "";
        
        style.cssText = style.cssText.replaceAll(" ", "").replace(originStyles, "") + ";" + styles;
        (this.resizeTarget as any)[storeKey] = styles;
        this.positionResizerToTarget(this.resizeTarget);
        this.options?.onChange?.(this.resizeTarget);
    }

    private toolbarInputChange(e: Event): void {
        const target = e.target as HTMLInputElement;
        const type = target?.dataset?.type;
        const value = target.value;
        if (type && Number(value)) {
            this._setStylesForToolbar(type, "width: " + Number(value) + "%;");
        }
    }

    private toolbarAltChange(e: Event): void {
        const target = e.target as HTMLInputElement;
        const type = target?.dataset?.type;
        if (type === "alt") {
            const value = target.value;
            this.resizeTarget.setAttribute("alt", value);
            this.options?.onChange?.(this.resizeTarget);
        }
    }

    private toolbarClick(e: Event): void {
        const target = e.target as HTMLElement;
        const type = target?.dataset?.type;
        if (type && target.classList.contains("btn")) {
            this._setStylesForToolbar(type, target?.dataset?.styles || "");
        }
    }

    private startResize(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        if (target.classList.contains("handler") && e.which === 1) {
            this.startResizePosition = {
                left: e.clientX,
                top: e.clientY,
                width: this.resizeTarget.clientWidth,
                height: this.resizeTarget.clientHeight,
            };
        }
    }

    private endResize(): void {
        this.startResizePosition = null;
        this.options?.onChange?.(this.resizeTarget);
    }

    private resizing(e: MouseEvent): void {
        if (!this.startResizePosition) return;
        
        const deltaX = e.clientX - this.startResizePosition.left;
        const deltaY = e.clientY - this.startResizePosition.top;
        let width = this.startResizePosition.width;
        let height = this.startResizePosition.height;
        
        width += deltaX;
        height += deltaY;
        
        if (e.altKey && (this.resizeTarget as any).originSize) {
            const originSize = (this.resizeTarget as any).originSize;
            const rate = originSize.height / originSize.width;
            height = rate * width;
        }
        
        this.resizeTarget.style.setProperty("width", Math.max(width, 30) + "px");
        this.resizeTarget.style.setProperty("height", Math.max(height, 30) + "px");
        this.positionResizerToTarget(this.resizeTarget);
    }

    public destroy(): void {
        if (this.resizer && this.container.contains(this.resizer)) {
            this.container.removeChild(this.resizer);
        }
        window.removeEventListener("mouseup", this.endResize);
        window.removeEventListener("mousemove", this.resizing);
        this.editor.removeEventListener('scroll', this.onScroll);
        this.resizer = null;
    }
}

class Iframe {
    element: HTMLElement;
    cb: () => void;
    hasTracked: boolean = false;

    constructor(element: HTMLElement, cb: () => void) {
        this.element = element;
        this.cb = cb;
    }
}

class IframeClick {
    static resolution: number = 200;
    static iframes: Iframe[] = [];
    static interval: NodeJS.Timeout | null = null;

    static track(element: HTMLElement, cb: () => void): void {
        this.iframes.push(new Iframe(element, cb));
        if (!this.interval) {
            this.interval = setInterval(() => {
                IframeClick.checkClick();
            }, this.resolution);
        }
    }

    static checkClick(): void {
        if (document.activeElement) {
            const activeElement = document.activeElement;
            for (const iframe of this.iframes) {
                if (activeElement === iframe.element) {
                    if (!iframe.hasTracked) {
                        iframe.cb();
                        iframe.hasTracked = true;
                    }
                } else {
                    iframe.hasTracked = false;
                }
            }
        }
    }
}

function QuillResizeImage(quill: any, options?: QuillResizeImageOptions) {
    const container = quill.root;
    let resizeTarget: HTMLElement | null;
    let resizePlugin: ResizePlugin | null;
    
    function triggerTextChange() {
        const Delta = quill.getContents().constructor;
        const delta = new Delta().retain(1);
        quill.updateContents(delta);
    }
    
    container.addEventListener("click", (e: Event) => {
        const target = e.target as HTMLElement;
        if (target && ["img", "video"].includes(target.tagName.toLowerCase())) {
            resizeTarget = target;
            resizePlugin = new ResizePlugin(target, container.parentElement, container, {
                ...options,
                onChange: triggerTextChange
            });
        }
    });
    
    quill.on("text-change", () => {
        // iframe 大小调整
        container.querySelectorAll("iframe").forEach((item: HTMLElement) => {
            IframeClick.track(item, () => {
                resizeTarget = item;
                resizePlugin = new ResizePlugin(item, container.parentElement, container, {
                    ...options,
                    onChange: triggerTextChange
                });
            });
        });
    });
    
    document.addEventListener("mousedown", (e: Event) => {
        const target = e.target as HTMLElement;
        if (target !== resizeTarget && 
            !(resizePlugin?.resizer?.contains(target))) {
            resizePlugin?.destroy();
            resizePlugin = null;
            resizeTarget = null;
        }
    }, { capture: true });
}

export default QuillResizeImage;
